import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TogglePill from '../ui/TogglePill';
import apiClient from '../../utils/api';

// "Try this" (You page, 2026-10 restyle). A short menu of ideas, each one line
// of what and one line of why. A green dot means we saw it in your own
// numbers; a grey dot means it's a coach idea we haven't seen in your data.
// Saying no sits right next to saying yes, at the same weight.

const parseCoach = (text) => {
  if (!text) return [];
  try {
    const obj = JSON.parse(text);
    if (obj && Array.isArray(obj.focus)) return obj.focus;
  } catch (_) { /* legacy paragraph: nothing to list */ }
  return [];
};

const Row = ({ what, why, seen, children }) => (
  <div className="grid grid-cols-1 items-center gap-3 px-5 py-4 sm:grid-cols-[1fr_auto] sm:gap-4">
    <div className="min-w-0">
      <div className="text-[1.1rem] font-medium leading-[1.3] tracking-[-0.01em]">{what}</div>
      {why && (
        <div className="mt-1 flex items-start gap-2 text-sm text-fg-3">
          <i className={`mt-[7px] inline-block h-2 w-2 flex-none rounded-full ${seen ? 'bg-good-strong dark:bg-good' : 'bg-fg-3'}`} />
          <span>{why}</span>
        </div>
      )}
    </div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);

const TryThis = () => {
  const [menu, setMenu] = useState(null);
  const [coach, setCoach] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showPast, setShowPast] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/advice');
      setMenu(res.data);
    } catch (_) { setMenu(null); }
  }, []);

  useEffect(() => {
    load();
    const tz = Math.round(-new Date().getTimezoneOffset() / 60);
    apiClient.get(`/api/coaching/insight?tz_offset=${tz}`)
      .then((res) => setCoach(parseCoach(res.data?.insight_text)))
      .catch(() => setCoach([]));
  }, [load]);

  const answer = async (id, accept) => {
    setBusy(true);
    try {
      await apiClient.post(`/api/advice/${id}/${accept ? 'accept' : 'decline'}`);
      await load();
    } catch (_) { /* leave the row as it was */ } finally { setBusy(false); }
  };

  const offered = useMemo(() => (menu?.enabled ? menu.offered || [] : []), [menu]);
  const accepted = useMemo(() => (menu?.enabled ? menu.accepted || [] : []), [menu]);
  const closed = useMemo(() => (menu?.enabled ? menu.closed || [] : []), [menu]);
  const coachRows = coach.slice(0, 3);
  const total = offered.length + accepted.length + coachRows.length;
  if (total === 0) return null;

  return (
    <section className="mt-10">
      <div className="mb-3.5">
        <h2 className="text-[1.2rem] font-medium tracking-[-0.015em]">Try this</h2>
        <p className="mt-1 text-sm text-fg-3">{total === 1 ? 'One idea.' : `${total} ideas.`} Pick any, or none.</p>
      </div>
      <div className="divide-y divide-line rounded-xl border border-line">
        {offered.map((it) => (
          <Row key={it.id} what={it.headline} why={it.rationale || it.flag_note} seen={it.flag === 'supported'}>
            <TogglePill disabled={busy} onClick={() => answer(it.id, true)}>I’ll try it</TogglePill>
            <TogglePill disabled={busy} onClick={() => answer(it.id, false)}>Not now</TogglePill>
          </Row>
        ))}
        {accepted.map((it) => (
          <Row key={it.id} what={it.headline} why={`Trying it. We look back after ${it.horizon_days} days.`} seen={it.flag === 'supported'}>
            <TogglePill on disabled>Trying it</TogglePill>
          </Row>
        ))}
        {coachRows.map((it, i) => (
          <Row key={`coach-${i}`} what={it.title} why={it.detail || 'A coach idea. We haven’t seen it in your data yet.'} seen={false} />
        ))}
      </div>
      <p className="mt-2.5 flex items-center gap-2 text-sm text-fg-3">
        <i className="inline-block h-2 w-2 rounded-full bg-good-strong dark:bg-good" />
        Green dot means we saw it in your own numbers.
      </p>
      {closed.length > 0 && (
        <div className="mt-3">
          <button type="button" onClick={() => setShowPast((s) => !s)}
            className="bg-transparent p-0 text-sm font-medium text-fg-2 hover:text-fg">
            {showPast ? 'Hide what happened last time' : 'What happened last time'}
          </button>
          {showPast && (
            <div className="mt-2 divide-y divide-line rounded-xl border border-line">
              {closed.slice(0, 3).map((it) => (
                <Row key={it.id} what={it.headline} why={it.read?.note || 'Not taken up.'} seen={it.flag === 'supported'} />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default TryThis;
